class YGImageLazyLoader extends HTMLElement {
  getLazyLoader() {
    if (!this._lazyLoader) {
      this._lazyLoader = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.getAttribute('data-src');

            if (src) {
              img.src = src;
              img.removeAttribute('data-src');
            }

            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: "50px",
      });
    }
    return this._lazyLoader;
  }
}

class YGImage extends YGImageLazyLoader {
  constructor() {
    super();
    this.breakpointChangeHandler = () => this.applyResponsiveSrc();
    this.imageListeners = new Map();
    this.videoListeners = new Map();
  }

  connectedCallback() {
    this.setLoadingState();
    this.applyResponsiveSrc();
    this.watchImages();
    this.watchPreviewVideos();
    this.loaderImg();
    this.loaderPreviewVideos();
    this.watchBreakpointChange();
  }

  disconnectedCallback() {
    this.breakpointQuery?.removeEventListener?.('change', this.breakpointChangeHandler);
    this.breakpointQuery?.removeListener?.(this.breakpointChangeHandler);
    this.previewVideoObserver?.disconnect();
    this.removeImageListeners();
    this.removeVideoListeners();
  }

  get images() {
    return this.querySelectorAll('img[data-src]');
  }

  get allImages() {
    return this.querySelectorAll('img');
  }

  get previewVideos() {
    return this.querySelectorAll('video[data-video-preview]');
  }

  get allMedia() {
    return this.querySelectorAll('img, video[data-video-preview]');
  }

  get responsiveImages() {
    return this.querySelectorAll('img[data-src-pc][data-src-mobile]');
  }

  get responsivePreviewVideos() {
    return this.querySelectorAll('video[data-video-preview][data-src-pc][data-src-mobile]');
  }

  get isMobileBreakpoint() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 999px)').matches);
  }

  setLoadingState() {
    this.classList.add('is-loading');
    this.classList.remove('is-loaded', 'is-error');
  }

  watchImages() {
    this.allImages.forEach((img) => this.watchImage(img));
  }

  watchPreviewVideos() {
    this.previewVideos.forEach((video) => this.watchPreviewVideo(video));
  }

  watchImage(img) {
    if (this.imageListeners.has(img)) return;

    const handleLoad = () => {
      if (img.hasAttribute('data-src')) return;
      this.handleImageLoad(img);
    };
    const handleError = () => {
      if (img.hasAttribute('data-src')) return;
      this.setErrorState(img);
    };

    this.imageListeners.set(img, { handleLoad, handleError });
    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);

    if (img.hasAttribute('data-src') || !img.complete) return;

    if (img.naturalWidth > 0) {
      this.handleImageLoad(img);
    } else {
      this.setErrorState(img);
    }
  }

  async handleImageLoad(img) {
    if (img.classList.contains('is-loaded')) return;

    if (typeof img.decode === 'function') {
      try {
        await img.decode();
      } catch (error) {
        // A successful load can still be rendered when decode() rejects.
      }
    }

    this.setLoadedState(img);
  }

  watchPreviewVideo(video) {
    if (this.videoListeners.has(video)) return;

    const handleLoadedMetadata = () => {
      if (video.duration && video.currentTime === 0) {
        try {
          video.currentTime = Math.min(0.001, video.duration);
        } catch (error) {
          // Some browsers paint frame zero without allowing an initial seek.
        }
      }
    };
    const handleLoadedData = () => this.handlePreviewVideoFrame(video);
    const handleError = () => this.setPreviewVideoErrorState(video);

    this.videoListeners.set(video, { handleLoadedMetadata, handleLoadedData, handleError });
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('error', handleError);

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      this.handlePreviewVideoFrame(video);
    }
  }

  handlePreviewVideoFrame(video) {
    if (video.classList.contains('is-loaded')) return;

    const activeSrc = video.dataset.activeSrc;
    let fallbackTimer;
    const reveal = () => {
      window.clearTimeout(fallbackTimer);
      if (video.dataset.activeSrc !== activeSrc) return;
      if (video.classList.contains('is-loaded')) return;
      video.pause();
      this.setLoadedState(video);
    };

    if (typeof video.requestVideoFrameCallback === 'function') {
      video.requestVideoFrameCallback(reveal);
      fallbackTimer = window.setTimeout(reveal, 250);
    } else {
      reveal();
    }
  }

  setLoadedState(img) {
    img.classList.add('is-loaded');
    this.removeImageListener(img);

    const isEveryMediaLoaded = Array.from(this.allMedia).every((media) => media.classList.contains('is-loaded'));
    if (!isEveryMediaLoaded) return;

    this.classList.remove('is-loading', 'is-error');
    this.classList.add('is-loaded');
  }

  setErrorState(img) {
    img.classList.add('is-error');
    this.removeImageListener(img);
    this.classList.remove('is-loading', 'is-loaded');
    this.classList.add('is-error');
  }

  setPreviewVideoErrorState(video) {
    video.classList.add('is-error');
    this.classList.remove('is-loaded');
    this.classList.add('is-loading', 'is-error');
  }

  removeImageListener(img) {
    const listeners = this.imageListeners.get(img);
    if (!listeners) return;

    img.removeEventListener('load', listeners.handleLoad);
    img.removeEventListener('error', listeners.handleError);
    this.imageListeners.delete(img);
  }

  removeImageListeners() {
    this.imageListeners.forEach((listeners, img) => {
      img.removeEventListener('load', listeners.handleLoad);
      img.removeEventListener('error', listeners.handleError);
    });
    this.imageListeners.clear();
  }

  removeVideoListener(video) {
    const listeners = this.videoListeners.get(video);
    if (!listeners) return;

    video.removeEventListener('loadedmetadata', listeners.handleLoadedMetadata);
    video.removeEventListener('loadeddata', listeners.handleLoadedData);
    video.removeEventListener('error', listeners.handleError);
    this.videoListeners.delete(video);
  }

  removeVideoListeners() {
    this.videoListeners.forEach((listeners, video) => {
      video.removeEventListener('loadedmetadata', listeners.handleLoadedMetadata);
      video.removeEventListener('loadeddata', listeners.handleLoadedData);
      video.removeEventListener('error', listeners.handleError);
    });
    this.videoListeners.clear();
  }

  applyResponsiveSrc() {
    this.responsiveImages.forEach((img) => {
      const url = this.isMobileBreakpoint ? img.dataset.srcMobile : img.dataset.srcPc;
      if (!url) return;
      if (img.hasAttribute('data-src')) {
        img.setAttribute('data-src', url);
      } else if (img.getAttribute('src') !== url) {
        img.src = url;
      }
    });

    this.responsivePreviewVideos.forEach((video) => {
      const url = this.isMobileBreakpoint ? video.dataset.srcMobile : video.dataset.srcPc;
      if (!url || video.dataset.activeSrc === url) return;

      video.dataset.activeSrc = url;
      video.classList.remove('is-loaded', 'is-error');
      this.setLoadingState();

      if (video.hasAttribute('src')) {
        video.src = url;
        video.load();
      } else {
        video.setAttribute('data-src', url);
      }
    });
  }

  watchBreakpointChange() {
    if (!window.matchMedia) return;
    this.breakpointQuery = window.matchMedia('(max-width: 999px)');
    if (this.breakpointQuery.addEventListener) {
      this.breakpointQuery.addEventListener('change', this.breakpointChangeHandler);
    } else if (this.breakpointQuery.addListener) {
      this.breakpointQuery.addListener(this.breakpointChangeHandler);
    }
  }

  loaderImg() {
    this.images.forEach((img) => {
      if (this.getLazyLoader) {
        this.getLazyLoader().observe(img);
      } else {
        console.warn('YGUtils 未加载，使用备用图片加载方式');
        if (img.getAttribute('data-src')) {
          img.src = img.getAttribute('data-src');
          img.removeAttribute('data-src');
        }
      }
    });
  }

  loaderPreviewVideos() {
    if (!this.previewVideos.length) return;

    if (!window.IntersectionObserver) {
      this.previewVideos.forEach((video) => this.loadPreviewVideo(video));
      return;
    }

    this.previewVideoObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this.loadPreviewVideo(entry.target);
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: '50px',
    });

    this.previewVideos.forEach((video) => this.previewVideoObserver.observe(video));
  }

  loadPreviewVideo(video) {
    const src = video.getAttribute('data-src');
    if (!src) return;

    video.src = src;
    video.removeAttribute('data-src');
    video.load();
  }
}

if (!window.customElements.get('yg-image')) {
  window.customElements.define('yg-image', YGImage);
}

class YGVideoLazyLoader extends HTMLElement {
  getLazyLoader() {
    if (!this._lazyLoader) {
      this._lazyLoader = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.playVideo()
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: "50px",
      });
    }
    return this._lazyLoader;
  }
}
class YGUtils extends HTMLElement {
  // 防抖函数 - 延迟执行函数，避免频繁调用
  debounce(func, delay) {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }

  // 节流函数 - 限制函数执行频率
  throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  get #getCurrentSlidesPerView() {
    if (!this['breakpoints']) return 1;

    const screenWidth = window.innerWidth;

    // 将breakpoints按数值从小到大排序
    const sortedKeys = Object.keys(this['breakpoints']).map(Number).sort((a, b) => a - b);

    let current = sortedKeys[0]; // 默认取最小的

    // 遍历找到小于等于当前屏幕宽度的最大key
    for (let i = 0; i < sortedKeys.length; i++) {
      if (screenWidth >= sortedKeys[i]) {
        current = sortedKeys[i];
      } else {
        break;
      }
    }
  
    return this['breakpoints'][current].slidesPerView;
  }

  #getAllAttributes(dom) {
    let booleanAttrs = ['loop', 'pagination', 'navigation', 'scrollbar', 'centered-slides'];
    let jsonAttrs = ['breakpoints']
    for (const attr of dom.attributes) {
      if (attr.name.startsWith('data-')) {
        const cleanName = attr.name.slice(5); // 去除 'data-' 前缀
        
        let value = attr.value;
        if (booleanAttrs.includes(cleanName)) {
          value = value === 'true';
        } else if (jsonAttrs.includes(cleanName)) {
          value = this.#parseSwiperBreakpoints(value);
        }

        this[cleanName] = value;
      }
    }
  }

  #parseSwiperBreakpoints(str) {
    const result = {};
    const pairs = str.split(',');

    for (const pair of pairs) {
      const [breakpoint, values] = pair.split(':');
      if (!breakpoint || !values) continue;
  
      let [slides, space] = values.split('-').map(Number);
  
      if (this["col-is-auto"]) {
        slides = "auto"
      }
      result[breakpoint.trim()] = {
        slidesPerView: slides,
        spaceBetween: space
      };
    }

    return result;
  }

  initSwiper(dom, thumbnails) {
    this.#getAllAttributes(dom || this);
    const swiper = new window.Swiper(dom || this, {
      // 这里放置 swiper 的配置
      effect: this['effect'],
      freeMode: this['is-marquee']?"true":"false",
      loop: this['loop'],
      speed: this['speed'] || 300,
      centeredSlides: this['centered-slides'],
      autoplay: this['autoplay']?{
        delay: this['autoplay'],
        stopOnLastSlide: false,
        disableOnInteraction: false,
        waitForTransition: this['is-marquee']?"false":"true",
        pauseOnMouseEnter: this['is-marquee']?"false":"true",
      }:false,
      pagination: this['pagination']?{
        el: this['id']? `.${this['id']}-pagination`: '.swiper-pagination',
        type: this['pagination-type']?this['pagination-type']:'bullets',
        clickable: true,
      }:false,
      navigation: this['navigation']?{
        nextEl: this['id']? `.${this['id']}-next`:'.swiper-button-next',
        prevEl: this['id']? `.${this['id']}-prev`:'.swiper-button-prev',
      }:false,
      breakpoints: this['breakpoints']?this['breakpoints']:false,
      on: {
        init: e => {
          if (e.passedParams.loop) {
            let num = this.#getCurrentSlidesPerView;
            let sliderLength = e.slides.length;

            if (this['centered-slides']) {
              num += 4;
            }

            if(sliderLength <= num){
              for (let index = 1; index < Math.ceil(num / sliderLength); index++) {
                e.slides.forEach((slide) => {
                  const clonedElement = slide.cloneNode(true);
                  e.slidesEl.appendChild(clonedElement);
                })
              }

              setTimeout(() => {
                let $bullets = this.querySelectorAll('.swiper-pagination-bullet');
                $bullets.forEach((bullet, index) => {
                  if (index >= sliderLength) {
                    bullet.style.display = 'none';
                  }
                })
              }, 250)
            }
          }
        }
      },
      thumbs: {
        swiper: thumbnails
      }
    })

    return swiper;
  }

  getLatestDom(requestUrlOrCallback, callback) {
    // 支持两种调用方式：
    // 1. getLatestDom(callback) -> 使用当前页面 URL
    // 2. getLatestDom(requestUrl, callback)
    let requestUrl = requestUrlOrCallback;
    if (typeof requestUrlOrCallback === 'function') {
      callback = requestUrlOrCallback;
      requestUrl = window.location.href;
    } else {
      requestUrl = requestUrl || window.location.href;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    const promise = fetch(requestUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        if (typeof callback === 'function') {
          callback(html);
        }
        return html;
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
        // 将错误继续抛出，便于外部处理
        throw error;
      });

    // 返回 Promise，调用者可以选择使用回调或 await 返回值
    return promise;
  }
}
class YGSwiper extends YGUtils {
  constructor() {
    super();
    this.swiper = null;
  }
  async connectedCallback() {
    // 避免多次初始化
    if (this.swiper) return;

    try {
      if (!window.Swiper) {
        if (typeof window.loadMondoSwiper !== 'function') {
          throw new Error('Swiper loader is unavailable');
        }

        await window.loadMondoSwiper();
      }

      if (!this.isConnected || this.swiper) return;

      this.swiper = this.initSwiper(this);
    } catch (error) {
      console.error('Failed to initialize YGSwiper.', error);
    }
  }
}
if (!customElements.get('yg-swiper')) {
  customElements.define('yg-swiper', YGSwiper);
}

class YGSelect extends YGUtils {
  constructor() {
    super()
  }
  get sectionsEl() {
    if (this._sectionsEl) return this._sectionsEl;
    
    this._sectionsEl = {
      'current-option': this.querySelector('.yg-current-option'),
      'current-option-label': this.querySelector('.yg-current-option-label'),
      'options': this.querySelector('.yg-options'),
      'options-list': this.querySelectorAll('.yg-option').length > 0 ? this.querySelectorAll('.yg-option') : Array.from(this.querySelector('.yg-options').children),
      'body': document.body,
    };
    
    return this._sectionsEl;
  }
  connectedCallback() {
    this.sectionsEl['current-option']?.addEventListener('click', this.clickCurrentOption.bind(this));
    this.sectionsEl['body']?.addEventListener('click', this.clickBody.bind(this));
    this.sectionsEl['options-list']?.forEach((option) => {
      option.addEventListener('click', ()=>this.clickOption(option));
    });
  }
  clickCurrentOption() {
    this.openOptions();
  }
  clickOption(option) {
    this.sectionsEl['current-option-label'].innerText = option.innerText;
    if (option.dataset.value) {
      this.sectionsEl['current-option'].dataset.currentOption = option.dataset.value;
    }
  }
  clickBody(event) {
    if (!this.contains(event.target)) {
      this.cloneOptions();
    }
  }
  openOptions() {
    if (this.sectionsEl['options'].classList.contains('active')) {
      this.cloneOptions()
      return;
    };
    
    this.sectionsEl['options'].classList.add('active');
  }
  cloneOptions() {
    this.sectionsEl['options'].classList.remove('active') 
  }
}

if (!window.customElements.get('yg-select')) {
  window.customElements.define('yg-select', YGSelect);
}
